import { Box, Container, Grid } from "@mui/material";
import { CustomBannerSkeleton } from "../../../components/CustomBannerSkeleton/CustomBannerSkeleton";
import { Colors } from "../../../styles/constant";
import CustomShimmer from "../../../components/CustomShimmer/CustomShimmer";
import { times } from "lodash";
import useWindowSize from "../../../hooks/useWindowSize";
import { tabViewPoint } from "../../../utils/constants";
import AccordionBoxSkeleton from "./AccordionBoxSkeleton";
import CenterBannerSkeleton from "./CenterBannerSkeleton";
import FooterBannerSkeleton from "./FooterBannerSkeleton";
import NewOfferingSkeleton from "./NewOfferingSkeleton";
import ProductCardSkeleton from "./ProductCardSkeleton";
import TopBannerSkeleton from "./TopBannerSkeleton";

const DashboardSkeleton = () => {
  const { width } = useWindowSize();

  const desktopProductSections = [
    { itemCount: 8, gridItemSize: 3 },
    { itemCount: 5, gridItemSize: 4 },
  ];

  return width >= tabViewPoint ? (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        padding="1rem 2rem"
        sx={{ backgroundColor: Colors.bgGray }}
      >
        <CustomShimmer width="7.5rem" height="3rem" borderRadius=".25rem" />
        <Box display="flex" alignItems="center" gap="1rem">
          {times(4, (index) => (
            <CustomShimmer
              key={index}
              width="2rem"
              height="2rem"
              borderRadius="2rem"
            />
          ))}
          <CustomShimmer width="3rem" height="3rem" borderRadius="3rem" />
        </Box>
      </Box>
      <Grid container className="main-grid">
        <Grid item sm={4} md={4} sx={{ backgroundColor: Colors.white }}>
          <Box
            padding="3rem 2rem 0rem 2rem"
            display="flex"
            flexDirection="column"
            rowGap="1rem"
          >
            {times(2, (index) => (
              <CenterBannerSkeleton key={index} />
            ))}
          </Box>
          <Box marginX="1rem">
            <FooterBannerSkeleton />
          </Box>
          <Box marginX="2rem">
            <CenterBannerSkeleton />
          </Box>
        </Grid>
        <Grid item sm={8} md={8} className="right-side-grid">
          <Box padding="3rem 2rem 0rem 2rem">
            <Box marginBottom="1rem">
              <CustomShimmer
                width="100%"
                height="2rem"
                borderRadius="0.25rem"
              />
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <CustomShimmer
                  width="6rem"
                  height="1.125rem"
                  borderRadius=".25rem"
                />
                <CustomShimmer
                  width="12rem"
                  height="1rem"
                  borderRadius=".25rem"
                  sx={{ marginTop: ".5rem" }}
                />
              </Box>
              <CustomShimmer
                width="6rem"
                height="1.125rem"
                borderRadius=".25rem"
              />
            </Box>
            <Box
              sx={{ backgroundColor: Colors.bgGray }}
              padding="1rem"
              borderRadius=".5rem"
              marginTop="1rem"
            >
              <AccordionBoxSkeleton showContainer={false} />
            </Box>

            <Box display="flex" columnGap="1rem" marginY="2rem">
              {times(2, (index) => (
                <Box key={index} flex="1">
                  <TopBannerSkeleton backgroundColor={Colors.bgGray} />
                </Box>
              ))}
            </Box>
            <Box display="flex" columnGap="1rem">
              {desktopProductSections.map((section) => {
                return (
                  <Box key={section.itemCount}>
                    <CustomShimmer
                      width="70%"
                      height="1rem"
                      borderRadius=".25rem"
                    />
                    <Box
                      padding="1rem 1rem"
                      sx={{ backgroundColor: Colors.bgGray }}
                      marginTop="1rem"
                      borderRadius="1rem"
                    >
                      <Grid container rowGap=".75rem" marginTop="1rem">
                        {times(section.itemCount, (index) => {
                          return (
                            <Grid
                              item
                              xs={section.gridItemSize}
                              key={`${section.itemCount}-${index}`}
                            >
                              <ProductCardSkeleton />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  ) : (
    <Box sx={{ backgroundColor: Colors.bgGray }} paddingTop="1rem">
      <Container>
        <Box display="flex" justifyContent="end" alignItems="center" gap="1rem">
          <CustomShimmer width="2rem" height="2rem" borderRadius="2rem" />
          <CustomShimmer width="2.5rem" height="2.5rem" borderRadius="3rem" />
        </Box>

        <Box marginY="1rem" marginX=".5rem">
          <TopBannerSkeleton />
        </Box>
      </Container>

      <Box>
        <CustomShimmer width="100%" height="2rem" borderRadius="0.5rem" />
      </Box>

      <Box
        sx={{ backgroundColor: Colors.white }}
        marginTop="1rem"
        paddingY="1rem"
      >
        <Container sx={{ marginTop: "1rem" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" flexDirection="column" gap=".5rem" width="100%">
              <CustomShimmer width="25%" borderRadius=".25rem" />
              <CustomShimmer
                width="35%"
                height=".75rem"
                borderRadius=".25rem"
              />
            </Box>
            <CustomShimmer width="2rem" height="2rem" borderRadius="2rem" />
          </Box>

          <AccordionBoxSkeleton />

          <NewOfferingSkeleton />
        </Container>
        <Box
          marginY="1rem"
          sx={{ backgroundColor: Colors.bgGray }}
          padding="1rem"
        >
          <CenterBannerSkeleton />
        </Box>
        <Container>
          <Box marginTop="1.5rem">
            <CustomShimmer width="40%" height="1rem" borderRadius=".25rem" />
            <Box overflow="auto" display="flex" gap="1rem">
              {times(6, (index) => {
                return (
                  <Box key={index}>
                    <ProductCardSkeleton />
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Container>
        <FooterBannerSkeleton />
        <Box
          marginTop="1rem"
          sx={{ backgroundColor: Colors.bgGray }}
          padding="1rem"
        >
          <CenterBannerSkeleton />
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardSkeleton;
